/*
 * Sound, made with the Web Audio API: an ambience that follows the time of day and the weather
 * (wind and birds by day, crickets at night, rain), the engines of the aircraft near the
 * camera, and short interface sounds. Browsers only allow audio after a user gesture, so the
 * context starts on the first click or tap. Nearly all of it is synthesised; two recordings
 * (public/audio, see SAMPLES) give a fighter going by and a sonic boom their real sound.
 *
 * Engines are modelled the way a jet sounds (see EngineVoice): a broadband roar whose colour
 * opens up with thrust, a deep rumble, the fan's tone rising as the engines spool up (the
 * whine of an approach, the buzz of a take-off), the crackle of a jet close by at full power,
 * and for a helicopter the slap of its blades. Each of the few loudest aircraft gets a voice of
 * its own, placed left or right of the camera, pitched by the Doppler effect as it comes and
 * goes, duller the further away it is (the air takes the highs first) and heard over the
 * airfield's own echo. A fighter going by is heard through the recording of one (see
 * SoundScape.passes), timed so that its loudest moment comes as it is nearest, and one coming
 * in to land through the same recording fading away as it touches down; its synthesised voice
 * steps back meanwhile. Airliners and the rest keep their synthesised voices.
 */

import { DEFAULT_SOUND_MIX, type SoundMix } from './sound-mix';

export type Cue = 'click' | 'build' | 'cash' | 'alert' | 'sign' | 'error' | 'achievement';

export { DEFAULT_SOUND_MIX, type SoundMix };

const clamp01 = (v: number) => (Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 1);

/** Two seconds of noise to loop: white, or brown (integrated, deep rumble). */
function noiseBuffer(ctx: AudioContext, brown: boolean): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let last = 0;
  for (let i = 0; i < data.length; i++) {
    const white = Math.random() * 2 - 1;
    if (brown) {
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    } else {
      data[i] = white;
    }
  }
  return buffer;
}

/** Four seconds of pink noise (equal energy per octave: the colour of a jet's roar). */
function pinkBuffer(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 4, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  // Paul Kellet's filter.
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;
  for (let i = 0; i < data.length; i++) {
    const w = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
    b6 = w * 0.115926;
  }
  return buffer;
}

/** Two seconds of crackle: sparse, sharp pops (a jet's exhaust at full power, close by). */
function crackleBuffer(ctx: AudioContext): AudioBuffer {
  const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    if (Math.random() < 0.0016) {
      const amp = (0.4 + Math.random() * 0.6) * (Math.random() < 0.5 ? -1 : 1);
      const len = 6 + Math.floor(Math.random() * 30);
      for (let k = 0; k < len && i + k < data.length; k++) {
        data[i + k] = (data[i + k] ?? 0) + amp * Math.exp(-k / 5) * (k % 2 ? -1 : 1);
      }
    }
  }
  return buffer;
}

/**
 * The echo of a wide open airfield: a few seconds of stereo noise dying away, darker as it
 * goes (air soaks up the highs first), after a short gap before the first reflections.
 */
function echoImpulse(ctx: AudioContext): AudioBuffer {
  const seconds = 2.6;
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    let smooth = 0;
    for (let i = 0; i < length; i++) {
      const t = i / ctx.sampleRate;
      const k = Math.min(0.9, 0.15 + t * 0.35);
      smooth = smooth * k + (Math.random() * 2 - 1) * (1 - k);
      data[i] = t < 0.03 ? 0 : smooth * Math.exp(-t / 0.55);
    }
  }
  return buffer;
}

export type EngineKind = 'light' | 'narrow' | 'wide' | 'fighter' | 'heli';

/** What makes a sound of engines: the kind of aircraft and what it is doing, and where. */
export interface EngineSource {
  /** Stays the same for the same aircraft from frame to frame. */
  key: string;
  kind: EngineKind;
  /** 0 engines off – 1 full power (take-off); idle is about 0.15. */
  thrust: number;
  /** Tiles from the listener. */
  distance: number;
  /** −1 to the left of the camera, 1 to the right. */
  pan: number;
  /** Tiles a second towards the listener (negative: going away). */
  closing: number;
  /** Tiles a second, whichever way (0: standing, or not known). */
  speed: number;
  /** The same at normal game speed (how fast it really flies, whatever the game's speed). */
  airspeed: number;
  /** Coming in to land: seconds until it touches down (undefined: not on its approach). */
  landingIn?: number;
}

/** How each kind sounds (frequencies in Hz, levels relative). */
interface EngineSound {
  /** The roar's brightest at full power, its body (a broad peak), and how loud. */
  cut: number;
  body: number;
  roar: number;
  rumble: number;
  /** The fan's tone at full power (0: none) and how loud it rings. */
  fan: number;
  fanLevel: number;
  /** Exhaust crackle at full power, close by. */
  crackle: number;
  /** Blade slaps a second (a helicopter's main rotor; 0: none). */
  chop: number;
}

const ENGINE: Record<EngineKind, EngineSound> = {
  light: {
    cut: 6000,
    body: 1400,
    roar: 0.4,
    rumble: 0.18,
    fan: 3400,
    fanLevel: 0.06,
    crackle: 0,
    chop: 0,
  },
  narrow: {
    cut: 5200,
    body: 650,
    roar: 0.75,
    rumble: 0.4,
    fan: 1850,
    fanLevel: 0.05,
    crackle: 0.12,
    chop: 0,
  },
  wide: {
    cut: 4000,
    body: 420,
    roar: 0.95,
    rumble: 0.65,
    fan: 1300,
    fanLevel: 0.045,
    crackle: 0.18,
    chop: 0,
  },
  fighter: {
    cut: 8000,
    body: 640,
    roar: 1.05,
    rumble: 1,
    fan: 0,
    fanLevel: 0,
    crackle: 0.9,
    chop: 0,
  },
  heli: {
    cut: 2200,
    body: 700,
    roar: 0.32,
    rumble: 0.3,
    fan: 5600,
    fanLevel: 0.02,
    crackle: 0,
    chop: 18,
  },
};

/** The recordings (public/audio; both CC0, see credits.ts), decoded when the sound starts. */
const SAMPLES = { flyby: 'audio/jet-flyby.mp3', boom: 'audio/sonic-boom.mp3' } as const;
type SampleName = keyof typeof SAMPLES;

/** Seconds into the flyby recording at which the jet is nearest (its pitch falling fastest). */
const FLYBY_NEAREST = 4;
/** Slower than this (tiles a second, at normal game speed) is taxiing: no flyby. */
const FLYBY_MIN_SPEED = 9;
/** Flybys heard at once, at most. */
const FLYBY_MAX = 3;
/**
 * A formation is heard as one: no second flyby for jets of the same flight nearest within this
 * many seconds of one already playing (a later pass of the same show has its own).
 */
const FLYBY_FORMATION = 2.5;
/** How much of a fighter's synthesised voice is left while its flyby plays. */
const FLYBY_DUCK = 0.3;
/** Within this (tiles, at its nearest) a pass is heard at full strength; further, softer. */
const FLYBY_NEAR = 45;
/**
 * A fighter coming in to land: its flyby starts this long before it touches down (seconds) and
 * this far into the recording, swells over the approach, and fades away over the last seconds
 * (LANDING_FADE) to touchdown.
 */
const LANDING_LEAD = 9;
const LANDING_SKIP = 1;
const LANDING_FADE = 3;
/** A fighter landing here is heard at this share of a pass (players found it far too loud). */
const LANDING_LEVEL = 0.3;
/**
 * The airport's own fighters (sorties taking off, flying off, coming back): their flyby only
 * when it passes within this many tiles, and no new one within OWN_PASS_SPACING seconds of the
 * last, so a busy military zone is not one long roar. Shows and flybys over are not held back.
 */
const OWN_PASS_FAR = 70;
const OWN_PASS_SPACING = 25;
/** The flight a jet flies in (the display jets' formations; anything else is on its own). */
const formationOf = (key: string) => /^(flyby|flypast|show)/.exec(key)?.[1] ?? key;

/** Seconds at least between two sonic booms: most fast passes boom, never one after another. */
const BOOM_SPACING = 40;

/** Speed of sound in tiles a second (343 m/s, about 5 m a tile). */
const SOUND_SPEED = 69;
/** How often the engine voices follow their aircraft (seconds). */
const ENGINE_UPDATE_SECONDS = 0.05;
/** Engine voices at once (the loudest aircraft). */
const VOICES = 5;
/** The fan's tones: blade passing and its neighbours (ratios to it, and levels). */
const FAN_RATIOS = [1, 1.5, 2.02];
const FAN_LEVELS = [1, 0.35, 0.35];

/** Louder close by: about inverse with distance, gentler near the source. */
const nearness = (distance: number) => (1 + distance / 18) ** -1.6;

/** How loud a source is, all things considered (for choosing which to give a voice). */
function engineLoudness(s: EngineSource): number {
  if (s.thrust <= 0) return 0;
  return (0.12 + 0.88 * s.thrust) * ENGINE[s.kind].roar * nearness(s.distance);
}

/**
 * The murmur of a crowd: pink noise shaped like many voices at once (the band of speech, a lift
 * where consonants are), its level stirred at the pace of syllables. Silent until a terminal
 * is near (see SoundScape.terminal).
 */
function crowdMurmur(
  ctx: AudioContext,
  pink: AudioBuffer,
  dry: AudioNode,
  wet: AudioNode,
): { gain: GainNode; pan: StereoPannerNode } {
  const src = ctx.createBufferSource();
  src.buffer = pink;
  src.loop = true;
  src.start(0, Math.random() * pink.duration);
  const speech = ctx.createBiquadFilter();
  speech.type = 'bandpass';
  speech.frequency.value = 650;
  speech.Q.value = 0.6;
  const presence = ctx.createBiquadFilter();
  presence.type = 'peaking';
  presence.frequency.value = 2600;
  presence.Q.value = 1;
  presence.gain.value = 5;
  // Syllables: a few overlapping talkers, their rhythms drifting in and out of step.
  const babble = ctx.createGain();
  babble.gain.value = 0.7;
  for (const rate of [3.7, 5.3, 6.9]) {
    const lfo = ctx.createOscillator();
    lfo.frequency.value = rate * (0.9 + Math.random() * 0.2);
    const depth = ctx.createGain();
    depth.gain.value = 0.12;
    lfo.connect(depth).connect(babble.gain);
    lfo.start();
  }
  const gain = ctx.createGain();
  gain.gain.value = 0;
  const pan = ctx.createStereoPanner();
  src.connect(speech).connect(presence).connect(babble).connect(gain).connect(pan).connect(dry);
  const send = ctx.createGain();
  send.gain.value = 0.6;
  pan.connect(send).connect(wet);
  return { gain, pan };
}

/**
 * Cars on the roads: the hiss of tyres on tarmac (pink noise, its top rolled off) over the low
 * drone of their engines. Silent until there is traffic near (see SoundScape.update).
 */
function roadHum(
  ctx: AudioContext,
  pink: AudioBuffer,
  brown: AudioBuffer,
  dry: AudioNode,
): { gain: GainNode; pan: StereoPannerNode } {
  const gain = ctx.createGain();
  gain.gain.value = 0;
  const pan = ctx.createStereoPanner();
  gain.connect(pan).connect(dry);
  for (const [buffer, type, frequency, level] of [
    [pink, 'lowpass', 1400, 1],
    [brown, 'lowpass', 220, 0.8],
  ] as const) {
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.start(0, Math.random() * buffer.duration);
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = frequency;
    const g = ctx.createGain();
    g.gain.value = level;
    src.connect(f).connect(g).connect(gain);
  }
  return { gain, pan };
}

/** One aircraft's engines (see the top of the file). */
class EngineVoice {
  key: string | null = null;
  private readonly roar: AudioBufferSourceNode;
  private readonly rumble: AudioBufferSourceNode;
  private readonly crackle: AudioBufferSourceNode;
  private readonly roarLow: BiquadFilterNode;
  private readonly roarBody: BiquadFilterNode;
  private readonly roarGain: GainNode;
  private readonly rumbleGain: GainNode;
  private readonly crackleGain: GainNode;
  private readonly fans: OscillatorNode[];
  private readonly fanGain: GainNode;
  private readonly chop: OscillatorNode;
  private readonly chopDepth: GainNode;
  private readonly out: GainNode;
  private readonly pan: StereoPannerNode;
  private readonly send: GainNode;

  constructor(
    private readonly ctx: AudioContext,
    buffers: { pink: AudioBuffer; brown: AudioBuffer; crackle: AudioBuffer },
    dry: AudioNode,
    wet: AudioNode,
  ) {
    const loop = (buffer: AudioBuffer) => {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      src.start(0, Math.random() * buffer.duration);
      return src;
    };
    const filter = (type: BiquadFilterType, frequency: number, q = 0.7, gain = 0) => {
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = frequency;
      f.Q.value = q;
      f.gain.value = gain;
      return f;
    };
    const gain = (value = 0) => {
      const g = ctx.createGain();
      g.gain.value = value;
      return g;
    };
    this.out = gain(0);
    this.pan = ctx.createStereoPanner();
    this.send = gain(0);
    this.out.connect(this.pan).connect(dry);
    this.pan.connect(this.send).connect(wet);

    // The roar: pink noise, its top opened by thrust and closed by distance, with a broad body.
    this.roar = loop(buffers.pink);
    this.roarLow = filter('lowpass', 2000, 0.5);
    this.roarBody = filter('peaking', 600, 0.6, 6);
    this.roarGain = gain(0);
    this.roar.connect(this.roarLow).connect(this.roarBody).connect(this.roarGain).connect(this.out);
    // The rumble under it.
    this.rumble = loop(buffers.brown);
    this.rumbleGain = gain(0);
    this.rumble
      .connect(filter('lowpass', 160, 0.7))
      .connect(this.rumbleGain)
      .connect(this.out);
    // Crackle: bright pops.
    this.crackle = loop(buffers.crackle);
    this.crackleGain = gain(0);
    this.crackle
      .connect(filter('highpass', 700, 0.5))
      .connect(this.crackleGain)
      .connect(this.out);
    // The fan: a few slightly detuned tones, wavering a little as a real engine's does.
    this.fanGain = gain(0);
    const fanBand = filter('bandpass', 2000, 1.2);
    fanBand.connect(this.fanGain).connect(this.out);
    this.fans = FAN_RATIOS.map((ratio, i) => {
      const osc = ctx.createOscillator();
      osc.type = i === 0 ? 'triangle' : 'sine';
      osc.frequency.value = 1000 * ratio;
      osc.detune.value = (Math.random() - 0.5) * 14;
      osc.connect(gain(FAN_LEVELS[i] ?? 0.3)).connect(fanBand);
      osc.start();
      return osc;
    });
    const waver = ctx.createOscillator();
    waver.frequency.value = 3.3 + Math.random() * 2;
    const waverDepth = gain(0.012);
    waver.connect(waverDepth).connect(this.fanGain.gain);
    waver.start();
    // Blade slap: the roar's level beaten by the rotor (helicopters only; depth 0 otherwise).
    this.chop = ctx.createOscillator();
    this.chop.type = 'square';
    this.chop.frequency.value = 18;
    this.chopDepth = gain(0);
    this.chop
      .connect(filter('lowpass', 90, 0.5))
      .connect(this.chopDepth)
      .connect(this.roarGain.gain);
    this.chop.start();
  }

  /** Follows its aircraft this frame (smoothed: no clicks). */
  set(s: EngineSource, level: number): void {
    const t = this.ctx.currentTime;
    const e = ENGINE[s.kind];
    const thrust = Math.max(0, Math.min(1, s.thrust));
    const on = thrust > 0 ? 1 : 0;
    const power = on * (0.12 + 0.88 * thrust ** 1.4);
    // The air takes the highs first.
    const air = 1 / (1 + s.distance / 90);
    const doppler = Math.max(
      0.78,
      Math.min(1.3, SOUND_SPEED / Math.max(20, SOUND_SPEED - s.closing * 0.8)),
    );
    const k = 0.08;
    this.out.gain.setTargetAtTime(power * nearness(s.distance) * 4.2 * level, t, k);
    this.pan.pan.setTargetAtTime(Math.max(-0.9, Math.min(0.9, s.pan)), t, k);
    // Far away, more of what is heard is the echo.
    this.send.gain.setTargetAtTime(0.25 + 0.6 * Math.min(1, s.distance / 90), t, 0.3);
    this.roarGain.gain.setTargetAtTime(e.roar * (0.35 + 0.65 * thrust), t, k);
    this.roarLow.frequency.setTargetAtTime(
      Math.max(250, e.cut * (0.4 + 0.6 * thrust) * air * doppler),
      t,
      0.15,
    );
    this.roarBody.frequency.setTargetAtTime(e.body * (0.8 + 0.4 * thrust) * doppler, t, 0.2);
    this.rumbleGain.gain.setTargetAtTime(e.rumble * thrust * thrust, t, k);
    // Crackle only close by and at power.
    const crackle = e.crackle * Math.max(0, thrust - 0.7) * 3.3 * Math.max(0, 1 - s.distance / 45);
    this.crackleGain.gain.setTargetAtTime(crackle, t, k);
    for (const src of [this.roar, this.rumble, this.crackle]) {
      src.playbackRate.setTargetAtTime(doppler, t, 0.15);
    }
    // The fan spools up with thrust; heard best as the whine of an approach, and lost quickly
    // with distance.
    if (e.fan > 0) {
      const f0 = e.fan * (0.5 + 0.5 * thrust) * doppler;
      this.fans.forEach((osc, i) => {
        osc.frequency.setTargetAtTime(f0 * (FAN_RATIOS[i] ?? 1), t, 0.25);
      });
      this.fanGain.gain.setTargetAtTime(
        on * e.fanLevel * (0.6 + 0.4 * thrust) * Math.exp(-s.distance / 40),
        t,
        k,
      );
    } else {
      this.fanGain.gain.setTargetAtTime(0, t, k);
    }
    this.chop.frequency.setTargetAtTime(Math.max(1, (e.chop || 18) * doppler), t, 0.2);
    this.chopDepth.gain.setTargetAtTime(e.chop ? e.roar * 0.6 : 0, t, k);
  }

  /** Nobody to follow: fade out. */
  silence(): void {
    this.out.gain.setTargetAtTime(0, this.ctx.currentTime, 0.25);
  }
}

class Loop {
  readonly gain: GainNode;
  /**
   * @param gusts how much the level and the filter swell and ease on their own, slowly and
   * unevenly (two slow waves that drift in and out of step): 0 for a steady sound.
   */
  constructor(
    ctx: AudioContext,
    buffer: AudioBuffer,
    filter: BiquadFilterNode,
    out: AudioNode,
    gusts = 0,
  ) {
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    const swell = ctx.createGain();
    swell.gain.value = 1;
    src.connect(filter).connect(swell).connect(this.gain).connect(out);
    src.start(0, Math.random() * buffer.duration);
    if (gusts > 0) {
      for (const [period, depth] of [
        [7.3, 0.45],
        [17.9, 0.35],
      ] as const) {
        const lfo = ctx.createOscillator();
        lfo.frequency.value = 1 / period;
        const level = ctx.createGain();
        level.gain.value = depth * gusts;
        lfo.connect(level).connect(swell.gain);
        const tone = ctx.createGain();
        tone.gain.value = filter.frequency.value * 0.6 * depth * gusts;
        lfo.connect(tone).connect(filter.frequency);
        lfo.start(ctx.currentTime + Math.random() * period);
      }
    }
  }

  set(ctx: AudioContext, value: number): void {
    this.gain.gain.setTargetAtTime(value, ctx.currentTime, 0.4);
  }
}

export interface SoundState {
  /** 0 at night, 1 by day. */
  dayness: number;
  /** 0–1 rain or snow intensity. */
  rain: number;
  /** 0–1: a sandstorm's gale (the wind roars, sand hisses past). */
  gale: number;
  /** The aircraft (and helicopters, and display jets) that can be heard. */
  engines: readonly EngineSource[];
  /**
   * The nearest terminal: how busy it is inside (0–1), how far it is (tiles) and where (−1 left
   * … 1 right). Null without one.
   */
  crowd: { busy: number; distance: number; pan: number } | null;
  /** The road traffic near the listener: how much of it (0–1) and on which side. */
  road: { level: number; pan: number } | null;
  /** The nearest building site at work: how far (tiles from its edge) and on which side. */
  site: { distance: number; pan: number } | null;
  /** Game running (paused: only the ambience). */
  running: boolean;
}

export class SoundScape {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private wind: Loop | null = null;
  private rain: Loop | null = null;
  private readonly voices: EngineVoice[] = [];
  /** When the engines were last followed (see update). */
  private enginesAt = 0;
  /** The murmur of the crowd in a terminal, and the next announcement chime. */
  private crowd: { gain: GainNode; pan: StereoPannerNode } | null = null;
  /** The hum of the cars on the roads near by (same voice as a crowd, other colour). */
  private road: { gain: GainNode; pan: StereoPannerNode } | null = null;
  private nextChime = 0;
  /** Where one-off sounds can also go to be heard through the airfield's echo. */
  private wet: AudioNode | null = null;
  /** The noise the ambience loops, kept for one-off sounds (thunder). */
  private white: AudioBuffer | null = null;
  private brown: AudioBuffer | null = null;
  private nextChirp = 0;
  /** When the nearest building site next makes itself heard. */
  private nextWork = 0;
  private volume = 0.7;
  /** The recordings, once decoded (until then, and if they fail, everything is synthesised). */
  private readonly samples: Partial<Record<SampleName, AudioBuffer>> = {};
  /** Jets going by close, heard through the flyby recording (see passes). */
  private flybys: {
    key: string;
    /** Its formation (the jets of one flight are heard as one). */
    group: string;
    src: AudioBufferSourceNode;
    gain: GainNode;
    pan: StereoPannerNode;
    /** When the jet is (or was) nearest. */
    nearestAt: number;
    /** When its synthesised voice may come back (a little before the recording ends). */
    until: number;
    /** Coming in to land (false: a pass). */
    landing: boolean;
    stopped: boolean;
  }[] = [];
  /** No sonic boom before this (see BOOM_SPACING). */
  private boomQuietUntil = 0;
  /** Aircraft that had their flyby lately: not again until this time (one per pass). */
  private readonly passedUntil = new Map<string, number>();
  /** No flyby of the airport's own fighters before this (see OWN_PASS_SPACING). */
  private ownPassQuietUntil = 0;

  constructor() {
    const start = () => this.start();
    window.addEventListener('pointerdown', start, { once: true });
    window.addEventListener('keydown', start, { once: true });
  }

  /** How loud each kind of sound is, on top of the volume (see SoundMix). */
  private mix: SoundMix = { ...DEFAULT_SOUND_MIX };

  setMix(mix: SoundMix): void {
    this.mix = {
      engines: clamp01(mix.engines),
      world: clamp01(mix.world),
      interface: clamp01(mix.interface),
    };
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.ctx && this.master) {
      this.master.gain.setTargetAtTime(this.volume * 0.6, this.ctx.currentTime, 0.05);
    }
  }

  private start(): void {
    if (this.ctx) return;
    try {
      const ctx = new AudioContext();
      this.ctx = ctx;
      // Everything through a gentle compressor: loud moments stay clean, quiet ones present.
      const glue = ctx.createDynamicsCompressor();
      glue.threshold.value = -16;
      glue.knee.value = 12;
      glue.ratio.value = 3;
      glue.attack.value = 0.01;
      glue.release.value = 0.25;
      glue.connect(ctx.destination);
      const master = ctx.createGain();
      master.gain.value = this.volume * 0.6;
      master.connect(glue);
      this.master = master;
      // The airfield's echo, fed by the engines and the one-off sounds.
      const echo = ctx.createConvolver();
      echo.buffer = echoImpulse(ctx);
      const echoLevel = ctx.createGain();
      echoLevel.gain.value = 0.35;
      echo.connect(echoLevel).connect(master);
      this.wet = echo;
      const white = noiseBuffer(ctx, false);
      const brown = noiseBuffer(ctx, true);
      this.white = white;
      this.brown = brown;
      const filter = (type: BiquadFilterType, frequency: number, q = 0.7) => {
        const f = ctx.createBiquadFilter();
        f.type = type;
        f.frequency.value = frequency;
        f.Q.value = q;
        return f;
      };
      this.wind = new Loop(ctx, brown, filter('lowpass', 520), master, 1);
      this.rain = new Loop(ctx, white, filter('bandpass', 3200, 0.4), master);
      const buffers = { pink: pinkBuffer(ctx), brown, crackle: crackleBuffer(ctx) };
      this.crowd = crowdMurmur(ctx, buffers.pink, master, echo);
      this.road = roadHum(ctx, buffers.pink, brown, master);
      for (let i = 0; i < VOICES; i++) {
        this.voices.push(new EngineVoice(ctx, buffers, master, echo));
      }
      void this.loadSamples(ctx);
    } catch {
      this.ctx = null; // No audio on this device: the game is silent.
    }
  }

  /** Fetches and decodes the recordings; one that fails stays synthesised. */
  private async loadSamples(ctx: AudioContext): Promise<void> {
    await Promise.all(
      (Object.keys(SAMPLES) as SampleName[]).map(async (name) => {
        try {
          const res = await fetch(`${import.meta.env.BASE_URL}${SAMPLES[name]}`);
          if (!res.ok) return;
          this.samples[name] = await ctx.decodeAudioData(await res.arrayBuffer());
        } catch {
          // Not this one: its synthesised version plays.
        }
      }),
    );
  }

  /** Called every frame with what is going on. */
  update(state: SoundState): void {
    const ctx = this.ctx;
    if (!ctx || !this.wind || !this.rain) return;
    const world = this.mix.world;
    this.wind.set(ctx, (0.05 + 0.04 * state.dayness + 0.2 * state.gale) * world);
    this.rain.set(ctx, (0.12 * state.rain + 0.035 * state.gale) * world);
    // Twenty times a second is plenty for the engines (each change is smoothed anyway), and
    // spares the page scheduling thousands of little changes a second.
    if (ctx.currentTime - this.enginesAt >= ENGINE_UPDATE_SECONDS) {
      this.enginesAt = ctx.currentTime;
      this.engines(state.running ? state.engines : []);
      this.passes(state.running ? state.engines : null);
      this.terminal(state.crowd);
      const road = this.road;
      if (road) {
        const t = ctx.currentTime;
        road.gain.gain.setTargetAtTime((state.road?.level ?? 0) * 0.35 * this.mix.world, t, 0.5);
        road.pan.pan.setTargetAtTime(Math.max(-0.7, Math.min(0.7, state.road?.pan ?? 0)), t, 0.5);
      }
    }
    if (state.running && state.site && ctx.currentTime > this.nextWork) this.works(state.site);
    // Birdsong by day, crickets at night, now and then.
    if (ctx.currentTime > this.nextChirp) {
      if (state.dayness > 0.5 && state.rain < 0.2 && state.gale < 0.3) this.bird();
      else if (state.dayness < 0.2) this.cricket();
      // Crickets keep at it through the night; birds sing now and then.
      this.nextChirp =
        ctx.currentTime +
        (state.dayness < 0.2 ? 0.5 + Math.random() * 1.2 : 1.5 + Math.random() * 4);
    }
  }

  /**
   * A building site near by at work: bursts of hammering on steel, now and then an angle
   * grinder's whine. Heard only close to (quickly fading with distance).
   */
  private works(site: { distance: number; pan: number }): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.white) return;
    const near = (1 + site.distance / 12) ** -2;
    this.nextWork = ctx.currentTime + 1.2 + Math.random() * 3;
    if (near < 0.03) return;
    const level = near * this.mix.world;
    const place = ctx.createStereoPanner();
    place.pan.value = Math.max(-0.8, Math.min(0.8, site.pan));
    place.connect(master);
    if (this.wet) {
      const send = ctx.createGain();
      send.gain.value = 0.5;
      place.connect(send).connect(this.wet);
    }
    const t = ctx.currentTime + 0.05;
    if (Math.random() < 0.8) {
      // A hammer on steel: a few strikes in a steady rhythm, each a clink with a dull thud.
      const strikes = 2 + Math.floor(Math.random() * 5);
      const every = 0.3 + Math.random() * 0.15;
      const pitch = 0.85 + Math.random() * 0.4;
      for (let i = 0; i < strikes; i++) {
        this.clink(
          place,
          t + i * every + Math.random() * 0.03,
          level * (0.8 + Math.random() * 0.3),
          pitch,
        );
      }
      return;
    }
    // An angle grinder biting into steel for a moment, its pitch wavering under the load.
    const length = 1 + Math.random() * 1.5;
    const src = ctx.createBufferSource();
    src.buffer = this.white;
    src.loop = true;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.Q.value = 6;
    band.frequency.setValueAtTime(5200, t);
    band.frequency.linearRampToValueAtTime(4300, t + length * 0.4);
    band.frequency.linearRampToValueAtTime(4800, t + length);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.09 * level, t + 0.15);
    g.gain.setValueAtTime(0.09 * level, t + length - 0.2);
    g.gain.linearRampToValueAtTime(0, t + length);
    src.connect(band).connect(g).connect(place);
    src.start(t, Math.random());
    src.stop(t + length + 0.05);
    this.nextWork += length;
  }

  /** One hammer strike on steel, at `at`: a bright clink ringing briefly over a dull thud. */
  private clink(out: AudioNode, at: number, level: number, pitch: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.white) return;
    const src = ctx.createBufferSource();
    src.buffer = this.white;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 3200 * pitch;
    band.Q.value = 3;
    const n = ctx.createGain();
    n.gain.setValueAtTime(0, at);
    n.gain.linearRampToValueAtTime(0.16 * level, at + 0.002);
    n.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
    src.connect(band).connect(n).connect(out);
    src.start(at, Math.random());
    src.stop(at + 0.08);
    for (const [ratio, gain, decay] of [
      [1, 0.05, 0.16],
      [2.7, 0.025, 0.08],
    ] as const) {
      const ring = ctx.createOscillator();
      ring.frequency.value = 1900 * pitch * ratio;
      const r = ctx.createGain();
      r.gain.setValueAtTime(0, at);
      r.gain.linearRampToValueAtTime(gain * level, at + 0.002);
      r.gain.exponentialRampToValueAtTime(0.0001, at + decay);
      ring.connect(r).connect(out);
      ring.start(at);
      ring.stop(at + decay + 0.02);
    }
    const thud = ctx.createOscillator();
    thud.frequency.setValueAtTime(220 * pitch, at);
    thud.frequency.exponentialRampToValueAtTime(110 * pitch, at + 0.05);
    const d = ctx.createGain();
    d.gain.setValueAtTime(0, at);
    d.gain.linearRampToValueAtTime(0.1 * level, at + 0.003);
    d.gain.exponentialRampToValueAtTime(0.0001, at + 0.08);
    thud.connect(d).connect(out);
    thud.start(at);
    thud.stop(at + 0.1);
  }

  /**
   * A terminal close by: its crowd murmurs (louder the busier it is), and now and then its
   * announcement chime rings out.
   */
  private terminal(crowd: SoundState['crowd']): void {
    const ctx = this.ctx;
    const voice = this.crowd;
    if (!ctx || !voice) return;
    const near = crowd ? (1 + crowd.distance / 10) ** -2 : 0;
    const level = crowd ? (0.2 + 0.8 * crowd.busy) * near : 0;
    voice.gain.gain.setTargetAtTime(level * 2.6 * this.mix.world, ctx.currentTime, 0.4);
    if (crowd)
      voice.pan.pan.setTargetAtTime(Math.max(-0.8, Math.min(0.8, crowd.pan)), ctx.currentTime, 0.3);
    if (crowd && near > 0.02 && ctx.currentTime > this.nextChime) {
      if (this.nextChime > 0) this.chime(Math.min(1, near * 4), crowd.pan);
      this.nextChime = ctx.currentTime + 45 + Math.random() * 75;
    }
  }

  /** The terminal's announcement chime: three soft notes, heard through the building. */
  private chime(volume: number, pan: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const place = ctx.createStereoPanner();
    place.pan.value = Math.max(-0.8, Math.min(0.8, pan));
    place.connect(master);
    if (this.wet) {
      const send = ctx.createGain();
      send.gain.value = 0.8;
      place.connect(send).connect(this.wet);
    }
    [659, 523, 784].forEach((f, i) => {
      const t = ctx.currentTime + i * 0.45;
      for (const [ratio, level, length] of [
        [1, 1, 1.4],
        [2, 0.3, 0.8],
        [3, 0.12, 0.5],
      ] as const) {
        const osc = ctx.createOscillator();
        osc.frequency.value = f * ratio;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.1 * volume * level * this.mix.world, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + length);
        osc.connect(g).connect(place);
        osc.start(t);
        osc.stop(t + length + 0.05);
      }
    });
  }

  /**
   * Gives the loudest aircraft a voice each: one already heard keeps its voice while it is
   * still among the loudest (no jumps), the rest fade out.
   */
  private engines(sources: readonly EngineSource[]): void {
    const ranked = sources
      .map((s) => ({ s, l: engineLoudness(s) }))
      .filter((x) => x.l > 0.002)
      .sort((a, b) => b.l - a.l)
      .slice(0, VOICES + 2);
    const wanted = new Map(ranked.slice(0, VOICES).map((x) => [x.s.key, x.s]));
    // Those heard already and still near the top keep their voice.
    for (const x of ranked.slice(VOICES)) {
      if (this.voices.some((v) => v.key === x.s.key)) wanted.set(x.s.key, x.s);
    }
    const free: EngineVoice[] = [];
    const now = this.ctx?.currentTime ?? 0;
    // An aircraft heard through its flyby recording keeps only a little of its own voice.
    const level = (key: string) =>
      this.mix.engines *
      (this.flybys.some((f) => f.group === formationOf(key) && f.until > now) ? FLYBY_DUCK : 1);
    for (const v of this.voices) {
      const s = v.key !== null ? wanted.get(v.key) : undefined;
      if (s) {
        v.set(s, level(s.key));
        wanted.delete(s.key);
      } else {
        if (v.key !== null) v.silence();
        v.key = null;
        free.push(v);
      }
    }
    for (const s of wanted.values()) {
      const v = free.shift();
      if (!v) break;
      v.key = s.key;
      v.set(s, level(s.key));
    }
  }

  /**
   * Fighters are heard through the flyby recording. Going by fast, it starts early enough that
   * its nearest moment (FLYBY_NEAREST) comes as the jet is nearest: flying straight on, that is
   * distance * closing / speed^2 seconds away, and it comes within
   * distance * sqrt(1 - (closing / speed)^2). Every pass is heard, a far one softer and duller,
   * and a formation as one. Coming in to land, it swells over the approach and fades away as
   * the jet touches down. Each is placed left or right as the jet goes. `sources` null: the
   * game stopped, and the flybys fade out.
   */
  private passes(sources: readonly EngineSource[] | null): void {
    const ctx = this.ctx;
    const master = this.master;
    const recording = this.samples.flyby;
    if (!ctx || !master) return;
    const t = ctx.currentTime;
    const stop = (f: (typeof this.flybys)[number], fade: number) => {
      if (f.stopped) return;
      f.stopped = true;
      f.gain.gain.setTargetAtTime(0, t, fade);
      f.src.stop(t + fade * 6);
    };
    for (const f of this.flybys) {
      const s = sources?.find((x) => x.key === f.key);
      if (!sources) {
        stop(f, 0.3);
        continue;
      }
      if (s) f.pan.pan.setTargetAtTime(Math.max(-0.85, Math.min(0.85, s.pan)), t, 0.1);
      if (!f.landing || f.stopped) continue;
      if (s?.landingIn === undefined) {
        // Down (or gone): what is left of it dies away.
        stop(f, 0.4);
        continue;
      }
      const fade = Math.max(0, Math.min(1, s.landingIn / LANDING_FADE));
      // Louder as it comes nearer, then gone as it touches down.
      f.gain.gain.setTargetAtTime(
        this.flybyLevel(s.distance, s.thrust) * fade * fade * LANDING_LEVEL,
        t,
        0.15,
      );
      f.until = t + 0.5;
    }
    for (const [key, until] of this.passedUntil) if (until < t) this.passedUntil.delete(key);
    if (!recording || !sources) return;
    for (const s of sources) {
      if (s.kind !== 'fighter' || this.flybys.length >= FLYBY_MAX) continue;
      if (this.passedUntil.has(s.key)) continue;
      let offset: number;
      let nearest: number;
      if (s.landingIn !== undefined) {
        // Coming in to land: from LANDING_LEAD seconds out (later, if it is late already).
        if (s.landingIn > LANDING_LEAD || s.landingIn < LANDING_FADE) continue;
        offset = LANDING_SKIP + (LANDING_LEAD - s.landingIn);
        nearest = s.distance;
      } else {
        if (s.airspeed < FLYBY_MIN_SPEED || s.closing <= 0) continue;
        const toNearest = (s.distance * s.closing) / (s.speed * s.speed);
        if (toNearest > FLYBY_NEAREST) continue;
        const along = Math.min(1, s.closing / s.speed);
        nearest = s.distance * Math.sqrt(1 - along * along);
        // Into the recording as far as the jet is already into its pass.
        offset = FLYBY_NEAREST - toNearest;
      }
      const length = recording.duration - offset;
      if (length < 3) continue;
      // The airport's own fighters: near passes only, spaced out.
      const own = s.key.startsWith('a');
      if (own && (t < this.ownPassQuietUntil || nearest > OWN_PASS_FAR)) continue;
      const group = formationOf(s.key);
      const nearestAt = t + FLYBY_NEAREST - offset;
      if (
        this.flybys.some(
          (f) => f.group === group && Math.abs(f.nearestAt - nearestAt) < FLYBY_FORMATION,
        )
      ) {
        continue;
      }
      this.passedUntil.set(s.key, t + length + 30);
      if (own) this.ownPassQuietUntil = t + OWN_PASS_SPACING;
      const near = Math.min(1, FLYBY_NEAR / Math.max(1, nearest));
      const landing = s.landingIn !== undefined;
      const src = ctx.createBufferSource();
      src.buffer = recording;
      src.playbackRate.value = 0.97 + Math.random() * 0.06;
      // The air takes the highs of a far pass.
      const air = ctx.createBiquadFilter();
      air.type = 'lowpass';
      air.frequency.value = Math.max(1500, 16000 * near);
      const gain = ctx.createGain();
      // (A landing swells in from silence: see above.)
      gain.gain.value = landing ? 0 : this.flybyLevel(nearest, s.thrust);
      const pan = ctx.createStereoPanner();
      pan.pan.value = Math.max(-0.85, Math.min(0.85, s.pan));
      src.connect(air).connect(gain).connect(pan).connect(master);
      if (this.wet) {
        const send = ctx.createGain();
        send.gain.value = 0.25 + 0.5 * (1 - near);
        pan.connect(send).connect(this.wet);
      }
      const flyby = {
        key: s.key,
        group,
        src,
        gain,
        pan,
        nearestAt,
        until: t + length - 3,
        landing,
        stopped: false,
      };
      this.flybys.push(flyby);
      src.onended = () => {
        this.flybys = this.flybys.filter((f) => f !== flyby);
        pan.disconnect();
      };
      src.start(t, offset);
    }
  }

  /** How loud a fighter's flyby is from this far (tiles): a far one softer, never lost. */
  private flybyLevel(distance: number, thrust: number): number {
    const near = Math.min(1, FLYBY_NEAR / Math.max(1, distance));
    return Math.max(0.1, near) * 0.85 * (0.6 + 0.4 * thrust) * this.mix.engines;
  }

  /**
   * A call made of a pure whistle that glides between pitches with a little vibrato, the way
   * small birds sing, placed somewhere left or right.
   */
  private whistle(
    at: number,
    from: number,
    to: number,
    duration: number,
    volume: number,
    pan: number,
    vibrato = 0,
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    osc.frequency.setValueAtTime(from, t);
    osc.frequency.exponentialRampToValueAtTime(to, t + duration);
    if (vibrato > 0) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 28 + Math.random() * 20;
      const depth = ctx.createGain();
      depth.gain.value = from * vibrato;
      lfo.connect(depth).connect(osc.frequency);
      lfo.start(t);
      lfo.stop(t + duration + 0.05);
    }
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(volume * this.mix.world, t + Math.min(0.02, duration * 0.3));
    gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    const place = ctx.createStereoPanner();
    place.pan.value = pan;
    osc.connect(gain).connect(place).connect(this.master);
    if (this.wet) place.connect(this.wet);
    osc.start(t);
    osc.stop(t + duration + 0.05);
  }

  /** A bird somewhere near: one of a few songs (a two-note whistle, a trill, a warble). */
  private bird(): void {
    const pan = Math.random() * 1.6 - 0.8;
    const base = 2400 + Math.random() * 1800;
    const v = 0.018 + Math.random() * 0.012;
    switch (Math.floor(Math.random() * 3)) {
      case 0: // "tee-yoo": up, then down
        this.whistle(0, base, base * 1.25, 0.14, v, pan, 0.01);
        this.whistle(0.18, base * 1.2, base * 0.8, 0.2, v, pan, 0.015);
        break;
      case 1: {
        // A trill: quick chirps, a little faster and lower each time.
        const n = 5 + Math.floor(Math.random() * 5);
        for (let i = 0; i < n; i++) {
          const f = base * (1.15 - i * 0.025);
          this.whistle(i * (0.075 - i * 0.002), f, f * 0.85, 0.05, v * 0.9, pan);
        }
        break;
      }
      default: // A warble falling away.
        this.whistle(0, base * 1.3, base * 0.7, 0.45, v, pan, 0.04);
    }
  }

  /** Crickets: a few quick buzzing pulses of a high tone, somewhere in the dark. */
  private cricket(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const pan = Math.random() * 1.6 - 0.8;
    const f = 4200 + Math.random() * 500;
    const pulses = 3 + Math.floor(Math.random() * 2);
    for (let i = 0; i < pulses; i++) {
      // Each pulse: the tone buzzed by a fast flutter of the wings.
      const t = ctx.currentTime + i * 0.06;
      const osc = ctx.createOscillator();
      osc.frequency.value = f;
      const flutter = ctx.createOscillator();
      flutter.type = 'square';
      flutter.frequency.value = 180;
      const am = ctx.createGain();
      am.gain.value = 0;
      const depth = ctx.createGain();
      depth.gain.value = 0.5;
      flutter.connect(depth).connect(am.gain);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, t);
      env.gain.linearRampToValueAtTime(0.08 * this.mix.world, t + 0.005);
      env.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);
      const place = ctx.createStereoPanner();
      place.pan.value = pan;
      osc.connect(am).connect(env).connect(place).connect(this.master);
      osc.start(t);
      flutter.start(t);
      osc.stop(t + 0.05);
      flutter.stop(t + 0.05);
    }
  }

  /** A radio's squelch: a faint, short burst of hiss as a transmission opens. */
  squelch(volume = 1): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || volume <= 0) return;
    const length = Math.floor(ctx.sampleRate * 0.05);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 2200;
    band.Q.value = 0.8;
    const gain = ctx.createGain();
    gain.gain.value = 0.045 * this.mix.interface;
    src.connect(band).connect(gain).connect(master);
    src.start();
  }

  /**
   * Thunder from a strike `distance` tiles away, arriving `delay` seconds from now: close by, a
   * sharp crack and a heavy roll; far off, only a low rumble.
   */
  thunder(distance: number, delay: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.white || !this.brown) return;
    const near = Math.max(0, Math.min(1, 1 - distance / 450));
    const t = ctx.currentTime + delay;
    const out = ctx.createGain();
    out.gain.value = (0.2 + 0.6 * near) * this.mix.world;
    out.connect(master);
    // Thunder rolls round the airfield: much of it through the echo.
    if (this.wet) out.connect(this.wet);
    if (near > 0.35) {
      // The crack: a burst of high, white noise.
      const crack = ctx.createBufferSource();
      crack.buffer = this.white;
      const high = ctx.createBiquadFilter();
      high.type = 'highpass';
      high.frequency.value = 700;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.45 * near, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      crack.connect(high).connect(g).connect(out);
      crack.start(t, Math.random());
      crack.stop(t + 0.45);
    }
    // The roll: deep noise through a closing low-pass, in a few uneven swells.
    const length = 3 + Math.random() * 3 + near * 1.5;
    const roll = ctx.createBufferSource();
    roll.buffer = this.brown;
    roll.loop = true;
    const low = ctx.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.setValueAtTime(220 + 520 * near, t);
    low.frequency.exponentialRampToValueAtTime(60, t + length);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.8, t + 0.05 + (1 - near) * 0.5);
    let at = t + 0.3;
    for (let i = 0; i < 4; i++) {
      at += (length / 5) * (0.6 + Math.random() * 0.8);
      g.gain.linearRampToValueAtTime(0.2 + Math.random() * 0.55, Math.min(at, t + length - 0.3));
    }
    g.gain.exponentialRampToValueAtTime(0.001, t + length);
    roll.connect(low).connect(g).connect(out);
    roll.start(t, Math.random());
    roll.stop(t + length + 0.1);
  }

  /**
   * A firework's burst, `delay` seconds from now: a sharp crack, a deep boom rolling back off
   * the buildings, then the crackle of the sparks.
   */
  bang(delay = 0, loudness = 1): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.white || !this.brown) return;
    const t = ctx.currentTime + delay;
    const out = ctx.createGain();
    out.gain.value = loudness * this.mix.world;
    out.connect(master);
    const echo = ctx.createGain();
    echo.gain.value = 0.9 * loudness;
    if (this.wet) echo.connect(this.wet);
    const burst = (
      buffer: AudioBuffer,
      type: BiquadFilterType,
      frequency: number,
      level: number,
      attack: number,
      decay: number,
      at: number,
    ) => {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = frequency;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(level, at + attack);
      g.gain.exponentialRampToValueAtTime(0.0005, at + attack + decay);
      src.connect(f).connect(g);
      g.connect(out);
      g.connect(echo);
      src.start(at, Math.random());
      src.stop(at + attack + decay + 0.05);
    };
    // The crack, then the boom.
    burst(this.white, 'highpass', 1800, 0.5, 0.002, 0.06, t);
    burst(this.brown, 'lowpass', 260, 1.0, 0.006, 1.3, t);
    // The sparks crackling as they burn: short ticks of bright noise, scattered.
    for (let i = 0; i < 16; i++) {
      const at = t + 0.3 + Math.random() * 1.6;
      burst(this.white, 'bandpass', 2500 + Math.random() * 3500, 0.06, 0.001, 0.025, at);
    }
  }

  /**
   * A finished building settling into place, one "tak" per step of its rise (`step` of
   * `steps`): a heavy thud with the clack of steel on top, rising a little in pitch each time,
   * and on the last a short bright chime. `loudness` 0–1 (how near the camera it is).
   */
  assemble(step: number, steps: number, loudness: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.white || loudness <= 0.01) return;
    const t = ctx.currentTime;
    const level = loudness * this.mix.world;
    const pitch = 1 + step * 0.12;
    const thud = ctx.createOscillator();
    thud.frequency.setValueAtTime(150 * pitch, t);
    thud.frequency.exponentialRampToValueAtTime(70 * pitch, t + 0.09);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.32 * level, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    thud.connect(g).connect(master);
    thud.start(t);
    thud.stop(t + 0.25);
    // The clack: a short band of bright noise and a ringing metal partial.
    const src = ctx.createBufferSource();
    src.buffer = this.white;
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = 2200 * pitch;
    band.Q.value = 2.5;
    const n = ctx.createGain();
    n.gain.setValueAtTime(0, t);
    n.gain.linearRampToValueAtTime(0.22 * level, t + 0.002);
    n.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
    src.connect(band).connect(n).connect(master);
    if (this.wet) n.connect(this.wet);
    src.start(t, Math.random());
    src.stop(t + 0.1);
    const ring = ctx.createOscillator();
    ring.type = 'triangle';
    ring.frequency.value = 1180 * pitch;
    const r = ctx.createGain();
    r.gain.setValueAtTime(0, t);
    r.gain.linearRampToValueAtTime(0.035 * level, t + 0.003);
    r.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    ring.connect(r).connect(master);
    ring.start(t);
    ring.stop(t + 0.2);
    if (step === steps - 1) {
      this.bell(1568, 0.12, 0.05 * Math.max(0.4, loudness), 0.9, 0.3);
      this.bell(2093, 0.22, 0.045 * Math.max(0.4, loudness), 1.2, 0.35);
    }
  }

  /**
   * A fast jet going by close (not again within BOOM_SPACING, unless `sure`): the double crack of its shock wave (the bow and the tail, a
   * tenth of a second apart) over a deep rumble rolling away, heard through the echo.
   * `loudness` 0–1 by how near it passed.
   */
  sonicBoom(loudness: number, sure = false): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.white || !this.brown || loudness <= 0.02) return;
    if (!sure && ctx.currentTime < this.boomQuietUntil) return;
    this.boomQuietUntil = ctx.currentTime + BOOM_SPACING;
    if (this.samples.boom) {
      this.recordedBoom(this.samples.boom, loudness);
      return;
    }
    const t = ctx.currentTime + 0.01;
    const out = ctx.createGain();
    out.gain.value = Math.min(1.1, loudness * 1.1) * this.mix.world;
    out.connect(master);
    const echo = ctx.createGain();
    echo.gain.value = 0.9;
    out.connect(echo);
    if (this.wet) echo.connect(this.wet);

    // The boom itself: the N-wave of the shock, twice (the nose's and the tail's), a sudden jump
    // in pressure falling straight through to as far below and snapping back. Felt as much as
    // heard: the double crack ("ba-BOOM") that makes people jump.
    const rate = ctx.sampleRate;
    const n = Math.floor(rate * 0.55);
    const wave = ctx.createBuffer(1, n, rate);
    const data = wave.getChannelData(0);
    for (const [start, length, peak] of [
      [0, 0.1, 1],
      [0.17, 0.11, 0.95],
    ] as const) {
      const s0 = Math.floor(start * rate);
      const len = Math.floor(length * rate);
      for (let i = 0; i < len && s0 + i < n; i++) data[s0 + i] = peak * (1 - (2 * i) / len);
    }
    const shock = ctx.createBufferSource();
    shock.buffer = wave;
    // Softened a touch by the distance it came (the air takes the sharpest edge off).
    const air = ctx.createBiquadFilter();
    air.type = 'lowpass';
    air.frequency.value = 2600;
    const shockLevel = ctx.createGain();
    shockLevel.gain.value = 1.1;
    shock.connect(air).connect(shockLevel).connect(out);
    shock.start(t);

    const burst = (
      buffer: AudioBuffer,
      type: BiquadFilterType,
      frequency: number,
      level: number,
      attack: number,
      decay: number,
      at: number,
      q = 0.7,
    ) => {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = decay > 1.8;
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = frequency;
      f.Q.value = q;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(level, at + attack);
      g.gain.exponentialRampToValueAtTime(0.0005, at + attack + decay);
      src.connect(f).connect(g).connect(out);
      src.start(at, Math.random());
      src.stop(at + attack + decay + 0.05);
      return g;
    };
    // The crack's bite and a thump under each shock.
    for (const at of [t, t + 0.17]) {
      burst(this.white, 'highpass', 1800, 0.5, 0.002, 0.07, at);
      burst(this.brown, 'lowpass', 160, 1.1, 0.004, 0.45, at);
    }
    // The body blow: a deep drop in pitch, under hearing almost, felt in the chest.
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(58, t);
    sub.frequency.exponentialRampToValueAtTime(24, t + 0.9);
    const subLevel = ctx.createGain();
    subLevel.gain.setValueAtTime(0, t);
    subLevel.gain.linearRampToValueAtTime(1.2, t + 0.02);
    subLevel.gain.exponentialRampToValueAtTime(0.001, t + 1.4);
    sub.connect(subLevel).connect(out);
    sub.start(t);
    sub.stop(t + 1.5);
    // Then it rolls away like thunder, back off the hills (two echoes, later and softer), with a
    // slow swell and fall through it.
    const rumble = burst(this.brown, 'lowpass', 110, 0.95, 0.25, 4.2, t + 0.12);
    const swell = ctx.createOscillator();
    swell.frequency.value = 1.6;
    const depth = ctx.createGain();
    depth.gain.value = 0.25;
    swell.connect(depth).connect(rumble.gain);
    swell.start(t);
    swell.stop(t + 4.6);
    burst(this.brown, 'lowpass', 90, 0.45, 0.3, 2.6, t + 0.9);
    burst(this.brown, 'lowpass', 75, 0.28, 0.4, 2.4, t + 1.8);
    // Windows and loose panels rattling in the blast.
    for (let i = 0; i < 7; i++) {
      const at = t + 0.2 + Math.random() * 0.6;
      burst(this.white, 'bandpass', 2600 + Math.random() * 2400, 0.22, 0.001, 0.03, at, 6);
    }
  }

  /**
   * The sonic boom recording (a fighter's), a little different each time: a far one duller
   * (the air takes the crack's edge), with a deep thump under it felt more than heard, and its
   * roll heard back off the hills through the echo.
   */
  private recordedBoom(recording: AudioBuffer, loudness: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t = ctx.currentTime + 0.01;
    const out = ctx.createGain();
    out.gain.value = Math.min(1, loudness) * this.mix.world;
    out.connect(master);
    if (this.wet) {
      const send = ctx.createGain();
      send.gain.value = 0.6;
      out.connect(send).connect(this.wet);
    }
    const src = ctx.createBufferSource();
    src.buffer = recording;
    src.playbackRate.value = 0.93 + Math.random() * 0.12;
    const air = ctx.createBiquadFilter();
    air.type = 'lowpass';
    air.frequency.value = 2200 + 9000 * Math.min(1, loudness);
    src.connect(air).connect(out);
    src.start(t);
    src.onended = () => out.disconnect();
    // The body blow (the recording, taken from a video, has little below hearing).
    const sub = ctx.createOscillator();
    sub.frequency.setValueAtTime(52, t + 0.15);
    sub.frequency.exponentialRampToValueAtTime(24, t + 1);
    const subLevel = ctx.createGain();
    subLevel.gain.setValueAtTime(0, t + 0.15);
    subLevel.gain.linearRampToValueAtTime(0.7, t + 0.17);
    subLevel.gain.exponentialRampToValueAtTime(0.001, t + 1.4);
    sub.connect(subLevel).connect(out);
    sub.start(t + 0.15);
    sub.stop(t + 1.5);
  }

  /**
   * A hot-air balloon's burner: the roar of the propane in a blast `seconds` long, `loudness`
   * 0–1 by how near: a deep rumble under a rushing roar and a hiss, swelling in and dying away.
   */
  burner(loudness: number, seconds: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.white || !this.brown || loudness <= 0.02) return;
    const t = ctx.currentTime + 0.01;
    const length = Math.max(0.5, seconds);
    const out = ctx.createGain();
    out.gain.value = Math.min(1, loudness) * this.mix.world;
    out.connect(master);
    const layer = (
      buffer: AudioBuffer,
      type: BiquadFilterType,
      frequency: number,
      q: number,
      level: number,
    ) => {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = frequency;
      f.Q.value = q;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(level, t + 0.12);
      g.gain.setValueAtTime(level, t + length);
      g.gain.exponentialRampToValueAtTime(0.0005, t + length + 0.45);
      src.connect(f).connect(g).connect(out);
      src.start(t, Math.random());
      src.stop(t + length + 0.5);
    };
    layer(this.brown, 'lowpass', 220, 0.7, 0.9);
    layer(this.white, 'bandpass', 900, 0.6, 0.22);
    layer(this.white, 'highpass', 3500, 0.5, 0.06);
  }

  /**
   * A flare fired from a jet: the hard pop of its charge with a thump under it, then the fizz
   * of it catching and burning (magnesium), falling away. `loudness` 0–1 by how near.
   */
  flare(loudness: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.white || loudness <= 0.02) return;
    const t = ctx.currentTime + 0.005;
    const out = ctx.createGain();
    out.gain.value = Math.min(1, loudness) * this.mix.world;
    out.connect(master);
    if (this.wet) {
      const send = ctx.createGain();
      send.gain.value = 0.6;
      out.connect(send).connect(this.wet);
    }
    const noise = (
      type: BiquadFilterType,
      frequency: number,
      q: number,
      level: number,
      attack: number,
      decay: number,
    ) => {
      const src = ctx.createBufferSource();
      src.buffer = this.white;
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.value = frequency;
      f.Q.value = q;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(level, t + attack);
      g.gain.exponentialRampToValueAtTime(0.0005, t + attack + decay);
      src.connect(f).connect(g).connect(out);
      src.start(t, Math.random());
      src.stop(t + attack + decay + 0.05);
    };
    // The pop: a crack of bright noise over a short, falling thump.
    noise('highpass', 1400 + Math.random() * 600, 0.7, 0.55, 0.001, 0.045);
    const thump = ctx.createOscillator();
    thump.frequency.setValueAtTime(150, t);
    thump.frequency.exponentialRampToValueAtTime(55, t + 0.08);
    const level = ctx.createGain();
    level.gain.setValueAtTime(0, t);
    level.gain.linearRampToValueAtTime(0.5, t + 0.003);
    level.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
    thump.connect(level).connect(out);
    thump.start(t);
    thump.stop(t + 0.16);
    // The fizz: a hiss rising in and burning down over a second or so.
    noise('bandpass', 3800 + Math.random() * 1600, 0.9, 0.09, 0.05, 1.1 + Math.random() * 0.5);
  }

  /**
   * A struck bell or chime at `frequency`: a few partials (the overtones of a small bell, not
   * whole multiples) ringing down, the high ones first, with a touch of the echo.
   */
  private bell(frequency: number, at: number, volume: number, ring = 0.9, echo = 0.25): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master) return;
    const t = ctx.currentTime + at;
    const out = ctx.createGain();
    out.gain.value = volume * this.mix.interface;
    out.connect(master);
    if (this.wet && echo > 0) {
      const send = ctx.createGain();
      send.gain.value = echo;
      out.connect(send).connect(this.wet);
    }
    for (const [ratio, level, decay] of [
      [1, 1, 1],
      [2.0, 0.45, 0.6],
      [2.76, 0.3, 0.45],
      [5.4, 0.12, 0.25],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.frequency.value = frequency * ratio;
      const g = ctx.createGain();
      const length = ring * decay;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(level, t + 0.004);
      g.gain.exponentialRampToValueAtTime(0.0001, t + length);
      osc.connect(g).connect(out);
      osc.start(t);
      osc.stop(t + length + 0.05);
    }
  }

  /**
   * A soft, dull knock (a low thud with a little noise on top): building, a stamp on paper.
   */
  private knock(frequency: number, at: number, volume: number, noise = 0.3): void {
    const ctx = this.ctx;
    const master = this.master;
    if (!ctx || !master || !this.white) return;
    const t = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    osc.frequency.setValueAtTime(frequency * 1.6, t);
    osc.frequency.exponentialRampToValueAtTime(frequency, t + 0.05);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(volume * this.mix.interface, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    osc.connect(g).connect(master);
    osc.start(t);
    osc.stop(t + 0.2);
    if (noise > 0) {
      const src = ctx.createBufferSource();
      src.buffer = this.white;
      const band = ctx.createBiquadFilter();
      band.type = 'bandpass';
      band.frequency.value = frequency * 8;
      band.Q.value = 1.2;
      const n = ctx.createGain();
      n.gain.setValueAtTime(0, t);
      n.gain.linearRampToValueAtTime(volume * noise * this.mix.interface, t + 0.002);
      n.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
      src.connect(band).connect(n).connect(master);
      src.start(t, Math.random());
      src.stop(t + 0.08);
    }
  }

  /** A short interface or event sound. */
  play(cue: Cue): void {
    switch (cue) {
      case 'click':
        // A soft, short tick, like a good switch.
        this.knock(1400, 0, 0.035, 0.6);
        break;
      case 'build':
        // Something set down firmly, then a small ring of metal.
        this.knock(110, 0, 0.22, 0.4);
        this.bell(880, 0.04, 0.03, 0.35, 0.1);
        break;
      case 'cash':
        // Two bright chimes, a fifth apart.
        this.bell(1318, 0, 0.07, 0.7);
        this.bell(1976, 0.09, 0.065, 0.9);
        break;
      case 'alert':
        // The airport's announcement chime: three notes down, unhurried.
        [784, 659, 523].forEach((f, i) => {
          this.bell(f, i * 0.32, 0.07, 1.2, 0.35);
        });
        break;
      case 'sign':
        // A pen across paper: a few quick, soft scratches.
        for (let i = 0; i < 6; i++) {
          this.knock(2600 + Math.random() * 900, i * 0.07 + Math.random() * 0.02, 0.03, 1.5);
        }
        break;
      case 'error':
        // A low, muted double knock: no.
        this.knock(140, 0, 0.14, 0.2);
        this.knock(120, 0.13, 0.12, 0.2);
        break;
      case 'achievement':
        // A rising major arpeggio on bells, the last held and shimmering.
        [523, 659, 784, 1047].forEach((f, i) => {
          this.bell(f, i * 0.1, i === 3 ? 0.07 : 0.05, i === 3 ? 2 : 0.6, 0.4);
        });
        this.bell(2093, 0.36, 0.02, 1.6, 0.5);
        break;
    }
  }
}

/** The one soundscape of the page. */
export const sound = new SoundScape();
